<template>
  <div
    v-if="groups && players"
    v-dragscroll.x
    v-on:dragscrollstart="handleUserScroll"
    v-on:dragscrollmove="handleUserScroll"
    v-on:dragscrollend="handleUserScroll"
    v-on:wheel="handleUserScroll"
    ref="player"
    class="player"
    v-on:mousedown="onMouseDown"
    v-on:mousemove="onMouseMove"
    v-on:mouseup="onMouseUp"
    :style="`height: ${height}px`"
  >
    <div class="player-inner" :style="playerInnerStyle" :key="updateKey">
      <div
        v-for="(group, index) in groups.keyboard"
        :key="`keyboard-${index}`"
        :ref="`group-keyboard-${index}`"
        class="group"
        :style="groupStyle(mode, group, true)"
        v-on:touchstart.prevent="handleGroupTouchStart"
        v-on:click="openGroup('keyboard', index)"
      >
        <div
          v-if="allImagesLoaded && mode === 0"
          :style="getGroupStyle(0, 'keyboard', index)"
        />
        <div
          v-if="allImagesLoaded && mode === 1"
          :style="getGroupStyle(1, 'keyboard', index)"
        />
      </div>
      <div
        v-for="(group, index) in groups.vocals"
        :key="`vocals-${index}`"
        :ref="`group-vocals-${index}`"
        class="group"
        :style="groupStyle(mode, group, false)"
        v-on:touchstart.prevent="handleGroupTouchStart"
        v-on:click="openGroup('vocals', index)"
      >
        <div
          v-if="allImagesLoaded && mode === 0"
          :style="getGroupStyle(0, 'vocals', index)"
        />
        <div
          v-if="allImagesLoaded && mode === 1"
          :style="getGroupStyle(1, 'vocals', index)"
        />
        <div
          v-if="allImagesLoaded && mode === 0"
          :ref="`group-curtain-${index}`"
          class="group-curtain"
        >
          <div />
          <div />
          <div />
          <div />
          <div />
          <div />
          <div />
          <div />
          <div />
          <div />
          <div />
          <div />
        </div>
      </div>
    </div>
    <div ref="maps" class="hidden">
      <img
        src="/images/groups/computer/keyboard/map_0.png"
        v-on:load="onMapImageLoaded"
      />
      <img
        src="/images/groups/computer/keyboard/map_1.png"
        v-on:load="onMapImageLoaded"
      />
      <img
        src="/images/groups/computer/vocals/map_0.png"
        v-on:load="onMapImageLoaded"
      />
      <img
        src="/images/groups/human/keyboard/map_0.png"
        v-on:load="onMapImageLoaded"
      />
      <img
        src="/images/groups/human/keyboard/map_1.png"
        v-on:load="onMapImageLoaded"
      />
      <img
        src="/images/groups/human/keyboard/map_2.png"
        v-on:load="onMapImageLoaded"
      />
      <img
        src="/images/groups/human/vocals/map_0.png"
        v-on:load="onMapImageLoaded"
      />
    </div>
    <div
      v-if="!allImagesLoaded"
      class="fixed top-0 right-0 bottom-0 left-0 flex justify-center items-center"
    >
      <h2>Loading {{ loadedPercent }}%</h2>
    </div>
  </div>
</template>

<script>
import { dragscroll } from "vue-dragscroll";
import score from "@/assets/data/score";
import computerKeyboardMap from "@/assets/data/computer-keyboard-map.json";
import computerVocalsMap from "@/assets/data/computer-vocals-map.json";
import humanKeyboardMap from "@/assets/data/human-keyboard-map.json";
import humanVocalsMap from "@/assets/data/human-vocals-map.json";

export default {
  name: "Player",

  directives: {
    dragscroll
  },

  props: {
    width: {
      type: Number,
      default: 100
    },
    height: {
      type: Number,
      default: 50
    },
    mode: {
      type: Number,
      default: 0
    },
    voiceIndex: {
      type: Number,
      default: 0
    },
    playing: {
      type: Boolean,
      default: false
    }
  },

  watch: {
    playing(newVal) {
      if (!newVal) {
        this.pause();
      } else {
        this.play();
        this.frameUpdateId = window.requestAnimationFrame(this.onFrameUpdate);
      }
    },

    voiceIndex() {
      this.$nextTick(() => {
        this.updateOpacities();
      });
    },

    mode() {
      this.$nextTick(() => {
        this.updateOpacities();
      });
    }
  },

  created() {
    window.addEventListener("resize", this.onWindowResize);
  },

  destroyed() {
    window.removeEventListener("resize", this.onWindowResize);
  },

  data() {
    return {
      players: null,
      updateKey: Date.now(),
      maps: {
        human: {
          keyboard: humanKeyboardMap,
          vocals: humanVocalsMap
        },
        computer: {
          keyboard: computerKeyboardMap,
          vocals: computerVocalsMap
        }
      },
      imagesLoaded: 0,
      totalImageCount: 7,
      allImagesLoaded: false,
      frameUpdateId: null,
      currentTime: 0,
      secondToPxMultiplier: 100,
      windowWidth: null,
      groups: null,
      minNote: null,
      maxNote: null,
      minOnset: null,
      maxOnset: null,
      mouseDown: false,
      mouseDownPos: null,
      dragging: false,
      intersectionOptions: {
        root: null,
        rootMargin: "0% 0px 0% -360px",
        thresholds: [0.0, 1.0]
      },
      minGroupOpacity: 0.5,
      maxGroupOpacity: 1,
      humanModeScalar: 1,
      groupClickStartTime: null
    };
  },

  async mounted() {
    this.windowWidth = window.innerWidth;

    // push vocal notes up to make room
    const vocalsNoteOffset = 13;
    score.vocals = score.vocals.map(x => {
      x.note += vocalsNoteOffset;
      return x;
    });

    // push bass keyboard notes up to close gap
    const bassNoteOffset = 8;
    score.keyboard = score.keyboard.map(x => {
      if (x.note <= 50) {
        x.note += bassNoteOffset;
      }
      return x;
    });

    const events = [...score.keyboard, ...score.vocals];

    this.minNote = this.getMinNote(events);
    this.maxNote = this.getMaxNote(events);
    this.minOnset = this.getMinOnset(events);
    this.maxOnset = this.getMaxOnset(events);
    this.groups = score;

    this.$nextTick(() => {
      this.players = window.players;
      for (let id in this.players) {
        this.players[id].addEventListener("ended", this.onAudioEnd);
      }
    });
  },

  computed: {
    loadedPercent() {
      return Math.round((this.imagesLoaded / this.totalImageCount) * 100);
    },

    playerInnerStyle() {
      const x = this.maxOnset * 0.001 * this.secondToPxMultiplier + 200;
      const w = x + this.windowWidth;
      return `width: ${w}px; height: 100%`;
    }
  },

  methods: {
    onMapImageLoaded() {
      this.imagesLoaded++;
      if (this.imagesLoaded === this.totalImageCount) {
        this.allImagesLoaded = true;
      }
    },

    onAudioEnd() {
      for (let id in this.players) {
        this.players[id].currentTime = 0;
      }

      window.cancelAnimationFrame(this.onFrameUpdate);

      this.currentTime = 0;
      this.scrollToTime();
      this.$emit("playChange", false);
    },

    play() {
      this.currentPlayer().currentTime = this.currentTime;
      this.currentPlayer().play();
      this.$emit("playChange", true);
    },

    pause() {
      window.cancelAnimationFrame(this.onFrameUpdate);

      for (let id in this.players) {
        this.players[id].pause();
      }

      this.$emit("playChange", false);
    },

    currentPlayer() {
      if (this.mode === 0) {
        return this.players[`audio-vocals-${this.voiceIndex}`];
      } else {
        return this.players["audio-original"];
      }
    },

    onFrameUpdate() {
      if (!this.playing) {
        return;
      }

      this.frameUpdateId = window.requestAnimationFrame(this.onFrameUpdate);

      this.currentTime = this.currentPlayer().currentTime;
      this.scrollToTime(this.currentTime);

      this.updateOpacities();
    },

    updateOpacities() {
      const xThresh = window.innerWidth * 0.5;
      for (let i = 0; i < this.groups.keyboard.length; i++) {
        const ref = this.$refs[`group-keyboard-${i}`][0];
        const viewportOffset = ref.getBoundingClientRect();
        const left = viewportOffset.left;
        if (this.mode === 1 || this.voiceIndex === 0) {
          ref.style.opacity = this.getOpacity(left, xThresh, false);
        } else {
          ref.style.opacity = 1 - this.minGroupOpacity;
        }
      }
      for (let i = 0; i < this.groups.vocals.length; i++) {
        if (this.mode === 0) {
          this.$refs[`group-vocals-${i}`][0].style.opacity = 1;
          const ref = this.$refs[`group-curtain-${i}`][0];
          const viewportOffset = ref.getBoundingClientRect();
          const left = viewportOffset.left;
          const opacity = this.getOpacity(left, xThresh, true);
          for (let j = 0; j < ref.children.length; j++) {
            if (this.voiceIndex === 0 || j + 1 === this.voiceIndex) {
              ref.children[j].style.opacity = opacity;
            } else {
              ref.children[j].style.opacity = 1 - this.minGroupOpacity;
            }
          }
        } else {
          const ref = this.$refs[`group-vocals-${i}`][0];
          const viewportOffset = ref.getBoundingClientRect();
          const left = viewportOffset.left;
          if (this.mode === 1 || this.voiceIndex === 0) {
            ref.style.opacity = this.getOpacity(left, xThresh, false);
          } else {
            ref.style.opacity = 1 - this.minGroupOpacity;
          }
        }

      }
    },

    getOpacity(left, xThresh, invert) {
      const attackPad = 10;
      const tailPad = 200;
      const oMin = invert ? 1 - this.minGroupOpacity : this.minGroupOpacity;
      const oMax = invert ? 1 - this.maxGroupOpacity : this.maxGroupOpacity;
      if (left >= xThresh && left < xThresh + attackPad) {
        let alpha = this.map(left, xThresh, xThresh + attackPad, 1, 0);
        alpha = alpha * (2 - alpha);
        return this.map(alpha, 0, 1, oMin, oMax);
      } else if (left > xThresh - tailPad && left < xThresh) {
        let alpha = this.map(left, xThresh - tailPad, xThresh, 0, 1);
        alpha = alpha * (2 - alpha);
        return this.map(alpha, 0, 1, oMin, oMax);
      } else {
        return oMin;
      }
    },

    scrollToTime(time) {
      const xScrollPos = time * this.secondToPxMultiplier;
      this.$refs["player"].scrollTo(xScrollPos, 0);
    },

    timeToScroll(xPos) {
      const time = xPos / this.secondToPxMultiplier;
      for (let id in this.players) {
        this.players[id].currentTime = time;
      }
      this.currentTime = time;
    },

    handleUserScroll() {
      this.pause();
      const x = this.$refs["player"].scrollLeft;
      this.timeToScroll(x);
      this.updateOpacities();
    },

    getMinNote(data) {
      return data.reduce(
        (min, p) => (p.note < min ? p.note : min),
        data[0].note
      );
    },

    getMaxNote(data) {
      return data.reduce(
        (max, p) => (p.note > max ? p.note : max),
        data[0].note
      );
    },

    getMinOnset(data) {
      return data.reduce(
        (min, p) => (p.onset < min ? p.onset : min),
        data[0].onset
      );
    },

    getMaxOnset(data) {
      return data.reduce(
        (max, p) => (p.onset > max ? p.onset : max),
        data[0].onset
      );
    },

    groupStyle(mode, group, small) {
      const h = this.height * 0.07;
      let groupHeight;
      if (mode === 1) {
        groupHeight = h * 0.8;
      } else if (small) {
        groupHeight = h;
      } else {
        groupHeight = h * 2.0;
      }

      const yfac = 1 - this.map(group.note, this.minNote, this.maxNote, 0, 1);
      let top = yfac * this.height - groupHeight * 0.5;
      const padding = h * 2;
      const paddedTop = this.map(
        top,
        0,
        this.height,
        padding,
        this.height - padding
      );

      const left = group.onset * 0.001 * this.secondToPxMultiplier;

      return `top: ${paddedTop}px; left: ${left}px; height:${groupHeight}px;`;
    },

    getGroupStyle(mode, type, index) {
      let source = mode === 1 ? "computer" : "human";

      // using source, type, and index, get:
      // - source image
      // - image x,y,w,h
      // - set element width/height
      // - set element backgroundImage
      // - debug with borders

      const map = this.maps[source][type];
      const data = map.lookup[index];

      const scalar = source === "human" ? this.humanModeScalar : 1;

      const width = scalar * this.secondToPxMultiplier;

      const imgSrc = `/images/groups/${source}/${type}/map_${data.b}.png`;

      const el = this.$refs[`group-${type}-${index}`][0];

      var cs = getComputedStyle(el);

      const paddingY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
      const borderY =
        parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
      const eh = el.offsetHeight - paddingY - borderY;
      const ew = (data.w / data.h) * eh;

      const bw = map.blocks[data.b].w;
      const bh = map.blocks[data.b].h;
      const sw = data.w;
      const sh = data.h;
      const sx = data.x;
      const sy = data.y;

      const mulw = ew / sw;
      const sbw = bw * mulw;
      const bgw = (sbw / ew) * 100;

      const mulh = eh / sh;
      const sbh = bh * mulh;
      const bgh = (sbh / eh) * 100;

      const bgx = (sx / (bw - sw)) * 100;
      const bgy = (sy / (bh - sh)) * 100;

      return `
        width: ${width}px;
        height: 100%;
        background-image: url(${imgSrc});
        background-size: ${bgw}% ${bgh}%;
        background-position: ${bgx}% ${bgy}%;
        background-repeat: no-repeat;
      `;
    },

    padStart(digits, num) {
      let str = num.toString();
      if (str.length >= digits) {
        return str;
      }

      const toAdd = digits - str.length;
      for (let i = 0; i < toAdd; i++) {
        str = "0" + str;
      }

      return str;
    },

    map(inVal, inMin, inMax, outMin, outMax) {
      return outMin + (outMax - outMin) * ((inVal - inMin) / (inMax - inMin));
    },

    onWindowResize() {
      this.windowWidth = window.innerWidth;
    },

    onMouseDown(event) {
      this.mouseDown = true;
      this.mouseDownPos = {
        x: event.clientX,
        y: event.clientY
      };
      this.$emit("mousedown");
    },

    onMouseMove(event) {
      if (this.mouseDown) {
        const pos = { x: event.clientX, y: event.clientY };
        const dist = Math.sqrt(
          Math.pow(this.mouseDownPos.x - pos.x, 2) +
            Math.pow(this.mouseDownPos.y - pos.y, 2)
        );
        if (dist > 3) {
          this.dragging = true;
        }
      }
    },

    onMouseUp() {
      this.mouseDown = false;
      this.mouseDownPos = null;
      setTimeout(() => {
        this.dragging = false;
      }, 10);
    },

    handleGroupTouchStart() {
      this.groupClickStartTime = Date.now();
    },

    openGroup(type, index) {
      if (this.groupClickStartTime !== null) {
        const diff = Date.now() - this.groupClickStartTime;
        if (diff > 300) {
          return;
        }
      }

      if (this.dragging) {
        return;
      }

      this.$emit("openNote", type, index);
    }
  }
};
</script>

<style scoped>
.player {
  position: relative;
  top: 0;
  left: 0;
  /* border: 1px solid #999999; */
  min-height: 200px;
  cursor: grab;
  overflow-x: auto;
}

.player::-webkit-scrollbar {
  width: 0 !important;
  height: 0 !important;
}

.player:active {
  cursor: grabbing;
}

.player-inner {
  position: relative;
  top: 0;
  left: 0;
  margin-left: calc(50vw - 190px);
  /* isolation: isolate; */
  background: white;
}

.group {
  position: absolute;
  width: auto;
  height: 15px;
  cursor: pointer;
  border: 1px solid transparent;
  padding: 0.25rem;
  /* mix-blend-mode: darken; */
}

/* .group.played {
  opacity: 1;
} */

.group img {
  max-width: initial;
  display: none;
}

.group:hover {
  background: white;
  border: 1px solid red;
  opacity: 1;
  z-index: 1;
  mix-blend-mode: normal;
}

.group-curtain {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 0.25rem;
  /* border: 1px solid blue; */
}

.group-curtain > div {
  position: relative;
  background: white;
  width: 100%;
  height: 8.333333%;
  /* border: 1px solid green; */
}
</style>
